import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// data: [{ day: "Aug 24", revenue: 4250 }, ...] — all 7 days of the
// selected date's Mon-Sun week, so the week reads on the x-axis for
// context, but every day except the selected one is forced to 0 by the
// caller — only that one day carries real revenue, so the line runs flat
// at 0 and rises just once, at the selected date. Still a single series,
// so no legend box is needed — the card title already names it.
const RevenueLineChart = ({ data = [], currency = "" }) => {
  if (!data.length) {
    return (
      <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF", fontSize: "14px" }}>
        No revenue data for this period
      </div>
    );
  }

  const fmtAmount = (v) => `${currency} ${Number(v || 0).toLocaleString()}`.trim();
  // Headroom above the peak so the line doesn't hug the plot's top edge.
  const maxRevenue = Math.max(...data.map((d) => Number(d.revenue) || 0), 0);
  const yMax = maxRevenue > 0 ? Math.ceil(maxRevenue * 1.25) : 10;

  // Marker only on the day that actually has revenue — every other point
  // sits at 0 just to draw the week's baseline, so dotting all of them
  // would be noise, not data.
  const renderDot = (props) => {
    const { cx, cy, payload, key } = props;
    if (!payload.revenue) return null;
    return <circle key={key} cx={cx} cy={cy} r={5} fill="#6366F1" stroke="#fff" strokeWidth={2} />;
  };

  // Recharts thins category-axis ticks by default once they can't all fit
  // in the available width, and no amount of angling/shrinking the labels
  // rescues that once a range gets long (two months of daily labels just
  // can't fit in a fixed-width card without either skipping some or
  // overlapping). So past a month, instead of ever skipping a date's
  // label, make the chart itself wider than its card and let the user
  // scroll horizontally — every day still gets its own label at readable
  // size, nothing is ever hidden.
  const manyTicks = data.length > 10;
  const scrollable = data.length > 31;
  const PIXELS_PER_TICK = 36;
  // Extra room past the last day's pixel position so its rotated label
  // (or, for the short unrotated case, its centered text) has space to
  // draw in full instead of being clipped by the chart's own SVG bounds —
  // that clipping, not any tick-skipping logic, is what was truncating the
  // rightmost date.
  const EDGE_BUFFER = manyTicks ? 30 : 8;
  const chartHeight = manyTicks ? 280 : 240;
  const chartWidth = scrollable ? data.length * PIXELS_PER_TICK + EDGE_BUFFER * 2 : "100%";

  return (
    <div style={{ width: "100%", overflowX: scrollable ? "auto" : "visible" }}>
      <div style={{ width: chartWidth, minWidth: "100%", height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 24, right: EDGE_BUFFER, left: EDGE_BUFFER, bottom: manyTicks ? 28 : 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fill: "#6B7280", fontSize: manyTicks ? 10 : 12 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              padding={{ left: 12, right: 12 }}
              angle={manyTicks ? -45 : 0}
              textAnchor={manyTicks ? "end" : "middle"}
              height={manyTicks ? 50 : 30}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fill: "#6B7280", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={48}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", fontSize: "12px" }}
              formatter={(v) => [fmtAmount(v), "Revenue"]}
            />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#6366F1"
              strokeWidth={2}
              dot={renderDot}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RevenueLineChart;
